import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, MessageCircle, Printer } from "lucide-react";
import { api, API, apiError, mediaUrl } from "@/lib/api";
import { fmtDate, fmtGNF, ORDER_STATUSES, PAYMENT_STATUS_STYLES, STATUS_STYLES, whatsappLink } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ABtn, AInput, ALabel, ASelect, Panel } from "./AdminLayout";

const WAREHOUSE_ALLOWED = ["Préparation", "Prête pour livraison", "Retournée"];

export default function AdminOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);

  const load = () =>
    api
      .get("/admin/orders", { params: { status: status || undefined, search: search || undefined } })
      .then((r) => setOrders(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    api.get("/admin/couriers").then((r) => setCouriers(r.data.filter((c) => c.is_active))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search]);

  const assignCourier = async (order, courierId) => {
    try {
      await api.put(`/admin/orders/${order.id}/courier`, { courier_id: courierId || null });
      toast.success(courierId ? "Livreur assigné" : "Livreur retiré");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changeStatus = async (order, newStatus) => {
    try {
      await api.put(`/admin/orders/${order.id}/status`, { status: newStatus, note: "Mise à jour depuis l'espace de gestion" });
      toast.success(`Statut mis à jour : ${newStatus}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changePayment = async (order, paymentStatus) => {
    try {
      await api.put(`/admin/orders/${order.id}/payment`, { payment_status: paymentStatus });
      toast.success("Paiement mis à jour");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const allowCashOnDelivery = async (order) => {
    if (!window.confirm(`Autoriser le paiement à la livraison pour ${order.number} ? Le stock sera réservé immédiatement.`)) return;
    try {
      await api.put(`/admin/orders/${order.id}/payment-method`, { payment_method: "cash_on_delivery" });
      toast.success("Paiement avant livraison autorisé pour cette commande");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const allowed = user?.role === "warehouse" ? WAREHOUSE_ALLOWED : ORDER_STATUSES;

  return (
    <Panel
      title="Commandes"
      subtitle={`${orders.length} commande(s)`}
      actions={
        <>
          <AInput placeholder="N°, client, téléphone…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-52" data-testid="admin-order-search" />
          <ASelect value={status} onChange={(e) => setStatus(e.target.value)} className="w-52" data-testid="admin-order-status-filter">
            <option value="">Tous les statuts</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </ASelect>
        </>
      }
      testid="admin-orders-page"
    >
      <div className="space-y-2">
        {orders.length === 0 && <p className="text-sm text-slate-500">Aucune commande trouvée.</p>}
        {orders.map((o) => (
          <div key={o.id} className="rounded-xl border border-slate-800 p-3" data-testid={`admin-order-row-${o.number}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-num text-sm font-bold text-white">{o.number}</p>
                <p className="text-[11px] text-slate-400">
                  {o.customer_name} · {o.phone} · {fmtDate(o.created_at)}
                </p>
                <p className="text-[11px] text-slate-400">
                  {o.zone_name} · {o.delivery_mode === "pickup" ? "Retrait entrepôt" : "Livraison"} · {o.payment_method_label}
                  {o.delivery_mode === "pickup" && o.pickup_date ? ` · Retrait ${o.pickup_date} ${o.pickup_slot || ""}` : ""}
                  {o.payment_reference ? ` · réf. ${o.payment_reference}` : ""}
                  {o.payment_phone ? ` · ${o.payment_phone}` : ""}
                  {o.stock_reserved ? " · stock réservé" : " · stock non réservé"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`ds-chip ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
                <span className={`ds-chip ${PAYMENT_STATUS_STYLES[o.payment_status] || ""}`}>{o.payment_status}</span>
                <span className="font-num text-sm font-bold text-emerald-400">{fmtGNF(o.total)}</span>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="w-56">
                <ALabel>Changer le statut</ALabel>
                <ASelect
                  value={o.status}
                  onChange={(e) => changeStatus(o, e.target.value)}
                  data-testid={`admin-order-status-select-${o.number}`}
                >
                  <option value={o.status}>{o.status}</option>
                  {allowed.filter((s) => s !== o.status).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </ASelect>
              </div>
              {user?.role === "admin" && (
                <div className="w-48">
                  <ALabel>Paiement</ALabel>
                  <ASelect
                    value={o.payment_status}
                    onChange={(e) => changePayment(o, e.target.value)}
                    data-testid={`admin-order-payment-select-${o.number}`}
                  >
                    {["en attente", "payé", "échoué", "remboursé"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </ASelect>
                </div>
              )}
              {user?.role === "admin" && o.payment_status !== "payé" && o.payment_method !== "cash_on_delivery" && (
                <ABtn variant="gold" onClick={() => allowCashOnDelivery(o)} data-testid={`admin-order-cod-${o.number}`}>
                  Autoriser paiement avant livraison
                </ABtn>
              )}
              <ABtn variant="ghost" onClick={() => setOpen(open === o.id ? null : o.id)} data-testid={`admin-order-toggle-${o.number}`}>
                {open === o.id ? "Masquer" : "Détail"}
              </ABtn>
              <div className="w-48">
                <ALabel>Livreur</ALabel>
                <ASelect
                  value={o.courier_id || ""}
                  onChange={(e) => assignCourier(o, e.target.value)}
                  data-testid={`admin-order-courier-select-${o.number}`}
                >
                  <option value="">Non assigné</option>
                  {couriers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} · {c.zone_name || "toutes zones"}</option>
                  ))}
                </ASelect>
              </div>
              <a
                href={whatsappLink(o)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#25D366] px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#1da851]"
                data-testid={`admin-order-whatsapp-${o.number}`}
              >
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp client
              </a>
              <ABtn variant="ghost" onClick={() => window.open(`/commande/${o.id}/bon-de-livraison`, "_blank")} data-testid={`admin-order-receipt-${o.number}`}>
                <Printer className="h-3.5 w-3.5" /> Bon de livraison
              </ABtn>
              <ABtn variant="ghost" onClick={() => window.open(`${API}/orders/${o.id}/receipt`, "_blank")} data-testid={`admin-order-pdf-${o.number}`}>
                <Download className="h-3.5 w-3.5" /> PDF
              </ABtn>
            </div>

            {open === o.id && (
              <div className="mt-3 space-y-2 border-t border-slate-800 pt-3" data-testid={`admin-order-detail-${o.number}`}>
                <p className="text-xs text-slate-400">Adresse : {o.address}</p>
                {o.note && <p className="text-xs text-slate-400">Note : {o.note}</p>}
                {o.items.map((it) => (
                  <div key={it.id} className="flex items-center gap-3 text-sm">
                    <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-slate-800">
                      {it.image && <img src={mediaUrl(it.image)} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <span className="min-w-0 flex-1 truncate text-slate-200">
                      {it.product_name}
                      {it.owner_type === "vendor" && (
                        <span className="ml-2 text-[11px] text-gold">vendeur : {it.owner_name}</span>
                      )}
                    </span>
                    <span className="font-num text-slate-400">{it.quantity} × {fmtGNF(it.unit_price)}</span>
                    <span className="font-num font-semibold text-white">{fmtGNF(it.total)}</span>
                  </div>
                ))}
                {o.vendor_breakdown?.length > 0 && (
                  <div className="rounded-xl border border-gold/40 bg-amber-950/20 p-3" data-testid={`admin-order-breakdown-${o.number}`}>
                    <p className="text-[11px] uppercase tracking-wider text-gold">Répartition multi-vendeurs</p>
                    {o.vendor_breakdown.map((v) => (
                      <div key={v.vendor_id} className="font-num mt-1 flex flex-wrap justify-between gap-2 text-xs">
                        <span className="text-slate-200">{v.vendor_name}</span>
                        <span className="text-slate-400">vente {fmtGNF(v.items_total)}</span>
                        <span className="text-gold">commission {fmtGNF(v.commission)}</span>
                        <span className="text-emerald-400">part vendeur {fmtGNF(v.vendor_amount)}</span>
                      </div>
                    ))}
                    <div className="font-num mt-2 flex justify-between border-t border-gold/30 pt-1 text-xs text-slate-300">
                      <span>Produits EMPIRE-SHOP</span>
                      <span>{fmtGNF(o.digital_shop_total || 0)}</span>
                    </div>
                  </div>
                )}
                <div className="font-num flex justify-end gap-4 text-xs text-slate-400">
                  <span>Sous-total {fmtGNF(o.subtotal)}</span>
                  <span>Livraison {fmtGNF(o.delivery_fee)}</span>
                  <span className="font-bold text-white">Total {fmtGNF(o.total)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}
